/**
 * Mirror of the backend's src/constants/partnerNetwork.ts.
 *
 * The values must stay identical — they travel over the wire in both
 * directions. Only the labels and the presentation hints below are frontend
 * concerns.
 */

export type ProspectStatus =
  | 'prospect'
  | 'contacted'
  | 'connected'
  | 'conversation'
  | 'interested'
  | 'meeting'
  | 'onboarding'
  | 'active_partner'
  | 'nurture'
  | 'not_interested';

/** The linear acquisition path. Nurture and Not interested are exits from it. */
export const PROSPECT_PIPELINE: ProspectStatus[] = [
  'prospect',
  'contacted',
  'connected',
  'conversation',
  'interested',
  'meeting',
  'onboarding',
  'active_partner',
];

export const PROSPECT_STATUSES: ProspectStatus[] = [
  ...PROSPECT_PIPELINE,
  'nurture',
  'not_interested',
];

export const PROSPECT_STATUS_LABELS: Record<ProspectStatus, string> = {
  prospect: 'Prospect',
  contacted: 'Contacted',
  connected: 'Connected',
  conversation: 'Conversation',
  interested: 'Interested',
  meeting: 'Meeting',
  onboarding: 'Onboarding',
  active_partner: 'Active partner',
  nurture: 'Nurture',
  not_interested: 'Not interested',
};

/** Badge colours. Warming through the pipeline, muted for the two exits. */
export const PROSPECT_STATUS_TONES: Record<ProspectStatus, string> = {
  prospect: 'border-zinc-500/40 bg-zinc-500/10 text-zinc-300',
  contacted: 'border-sky-500/40 bg-sky-500/10 text-sky-300',
  connected: 'border-cyan-500/40 bg-cyan-500/10 text-cyan-300',
  conversation: 'border-indigo-500/40 bg-indigo-500/10 text-indigo-300',
  interested: 'border-violet-500/40 bg-violet-500/10 text-violet-300',
  meeting: 'border-amber-500/40 bg-amber-500/10 text-amber-300',
  onboarding: 'border-orange-500/40 bg-orange-500/10 text-orange-300',
  active_partner: 'border-primary-500/40 bg-primary-500/10 text-primary-400',
  nurture: 'border-blue-500/30 bg-blue-500/10 text-blue-300',
  not_interested: 'border-zinc-600/40 bg-zinc-700/20 text-zinc-400',
};

export type PartnerCategory =
  | 'hospitality_consultant'
  | 'hotel_consultant'
  | 'restaurant_consultant'
  | 'business_consultant'
  | 'accountant'
  | 'pos_provider'
  | 'erp_consultant'
  | 'ecommerce_consultant'
  | 'digital_agency'
  | 'marketing_agency'
  | 'it_consultant'
  | 'hospitality_supplier'
  | 'technology_consultant'
  | 'business_advisor'
  | 'other';

export const PARTNER_CATEGORY_LABELS: Record<PartnerCategory, string> = {
  hospitality_consultant: 'Hospitality Consultant',
  hotel_consultant: 'Hotel Consultant',
  restaurant_consultant: 'Restaurant Consultant',
  business_consultant: 'Business Consultant',
  accountant: 'Accountant',
  pos_provider: 'POS Provider',
  erp_consultant: 'ERP Consultant',
  ecommerce_consultant: 'E-commerce Consultant',
  digital_agency: 'Digital Agency',
  marketing_agency: 'Marketing Agency',
  it_consultant: 'IT Consultant',
  hospitality_supplier: 'Hospitality Supplier',
  technology_consultant: 'Technology Consultant',
  business_advisor: 'Business Advisor',
  other: 'Other',
};

export const PARTNER_CATEGORIES = Object.keys(
  PARTNER_CATEGORY_LABELS
) as PartnerCategory[];

export type PartnerTier = 'tier_1' | 'tier_2' | 'tier_3';

export const PARTNER_TIER_LABELS: Record<PartnerTier, string> = {
  tier_1: 'Tier 1 — Strategic',
  tier_2: 'Tier 2 — Professional',
  tier_3: 'Tier 3 — Connector',
};

export const PARTNER_TIERS = Object.keys(PARTNER_TIER_LABELS) as PartnerTier[];

export type ProspectSource =
  | 'linkedin'
  | 'google'
  | 'referral'
  | 'event'
  | 'existing_network'
  | 'website'
  | 'other';

export const PROSPECT_SOURCE_LABELS: Record<ProspectSource, string> = {
  linkedin: 'LinkedIn',
  google: 'Google',
  referral: 'Referral',
  event: 'Event',
  existing_network: 'Existing Network',
  website: 'Website',
  other: 'Other',
};

export const PROSPECT_SOURCES = Object.keys(
  PROSPECT_SOURCE_LABELS
) as ProspectSource[];

export type PartnerStatus = 'pending' | 'active' | 'suspended' | 'rejected';

export const PARTNER_STATUS_LABELS: Record<PartnerStatus, string> = {
  pending: 'Pending approval',
  active: 'Active',
  suspended: 'Suspended',
  rejected: 'Rejected',
};

export const PARTNER_STATUS_TONES: Record<PartnerStatus, string> = {
  pending: 'border-amber-500/40 bg-amber-500/10 text-amber-300',
  active: 'border-primary-500/40 bg-primary-500/10 text-primary-400',
  suspended: 'border-orange-500/40 bg-orange-500/10 text-orange-300',
  rejected: 'border-red-500/40 bg-red-500/10 text-red-400',
};

export type ReferralStatus =
  | 'new'
  | 'contacted'
  | 'discovery'
  | 'qualified'
  | 'proposal'
  | 'negotiation'
  | 'won'
  | 'commission_due'
  | 'commission_paid'
  | 'lost';

export const REFERRAL_PIPELINE: ReferralStatus[] = [
  'new',
  'contacted',
  'discovery',
  'qualified',
  'proposal',
  'negotiation',
  'won',
  'commission_due',
  'commission_paid',
];

export const REFERRAL_STATUSES: ReferralStatus[] = [
  ...REFERRAL_PIPELINE,
  'lost',
];

export const REFERRAL_STATUS_LABELS: Record<ReferralStatus, string> = {
  new: 'New referral',
  contacted: 'Contacted',
  discovery: 'Discovery',
  qualified: 'Qualified',
  proposal: 'Proposal',
  negotiation: 'Negotiation',
  won: 'Won',
  commission_due: 'Commission due',
  commission_paid: 'Commission paid',
  lost: 'Lost / disqualified',
};

export const REFERRAL_STATUS_TONES: Record<ReferralStatus, string> = {
  new: 'border-sky-500/40 bg-sky-500/10 text-sky-300',
  contacted: 'border-cyan-500/40 bg-cyan-500/10 text-cyan-300',
  discovery: 'border-indigo-500/40 bg-indigo-500/10 text-indigo-300',
  qualified: 'border-violet-500/40 bg-violet-500/10 text-violet-300',
  proposal: 'border-amber-500/40 bg-amber-500/10 text-amber-300',
  negotiation: 'border-orange-500/40 bg-orange-500/10 text-orange-300',
  won: 'border-primary-500/40 bg-primary-500/10 text-primary-400',
  commission_due: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300',
  commission_paid: 'border-primary-500/50 bg-primary-500/20 text-primary-300',
  lost: 'border-zinc-600/40 bg-zinc-700/20 text-zinc-400',
};

export type CommissionStatus = 'pending' | 'due' | 'paid' | 'cancelled';

export const COMMISSION_STATUS_LABELS: Record<CommissionStatus, string> = {
  pending: 'Pending',
  due: 'Due',
  paid: 'Paid',
  cancelled: 'Cancelled',
};

export const COMMISSION_STATUS_TONES: Record<CommissionStatus, string> = {
  pending: 'border-zinc-500/40 bg-zinc-500/10 text-zinc-300',
  due: 'border-amber-500/40 bg-amber-500/10 text-amber-300',
  paid: 'border-primary-500/40 bg-primary-500/10 text-primary-400',
  cancelled: 'border-zinc-600/40 bg-zinc-700/20 text-zinc-400',
};

export type FollowUpFilter =
  | 'today'
  | 'tomorrow'
  | 'next3'
  | 'overdue'
  | 'none'
  | 'custom';

export const FOLLOW_UP_LABELS: Record<FollowUpFilter, string> = {
  today: 'Today',
  tomorrow: 'Tomorrow',
  next3: 'Next 3 days',
  overdue: 'Overdue',
  none: 'No follow-up set',
  custom: 'Custom range',
};

export type ActivityType =
  | 'prospect_created'
  | 'prospect_updated'
  | 'status_changed'
  | 'email_sent'
  | 'email_failed'
  | 'whatsapp_opened'
  | 'follow_up_scheduled'
  | 'note_added'
  | 'meeting_recorded'
  | 'partner_onboarded'
  | 'partner_registered'
  | 'partner_approved'
  | 'partner_suspended'
  | 'referral_submitted'
  | 'referral_updated'
  | 'commission_created'
  | 'commission_updated'
  | 'commission_paid';
